import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, ArrowLeft, Clock, Gamepad2 } from 'lucide-react';
import { useTimePlayed } from '../hooks';

const backToDashboardLink = (
  <Link
    to="/a/arche_game_rewards"
    className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
  >
    <ArrowLeft className="h-4 w-4 mr-1" />
    Back to Dashboard
  </Link>
);

function formatTime(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

function formatAvgSession(totalSeconds: number, sessionCount: number): string {
  if (sessionCount === 0) return '0m';
  return formatTime(Math.round(totalSeconds / sessionCount));
}

export function TimePlayedPage() {
  const { data, loading, error } = useTimePlayed();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <div>
          {backToDashboardLink}
          <h1 className="text-2xl font-semibold">Time Played</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-28 rounded-lg" />
          <Skeleton className="h-28 rounded-lg" />
          <Skeleton className="h-28 rounded-lg" />
        </div>
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <div>
          {backToDashboardLink}
          <h1 className="text-2xl font-semibold">Time Played</h1>
        </div>
        <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3">
          <AlertCircle className="h-4 w-4 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  const totalSeconds = data?.totalSeconds ?? 0;
  const totalSessions = data?.totalSessions ?? 0;
  const games = data?.games ?? [];

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div>
        {backToDashboardLink}
        <h1 className="text-2xl font-semibold">Time Played</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" /> Total Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatTime(totalSeconds)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
              <Gamepad2 className="h-4 w-4" /> Total Sessions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{totalSessions.toLocaleString()}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Avg Session</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatAvgSession(totalSeconds, totalSessions)}</p>
          </CardContent>
        </Card>
      </div>

      {games.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No time played data yet. Play some games to start tracking!
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Per-Game Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-medium">Game</th>
                    <th className="text-right py-2 font-medium">Time</th>
                    <th className="text-right py-2 font-medium">Sessions</th>
                    <th className="text-right py-2 font-medium">Avg Session</th>
                  </tr>
                </thead>
                <tbody>
                  {games.map((g) => (
                    <tr key={g.gameConfigId} className="border-b last:border-0">
                      <td className="py-2">{g.displayName}</td>
                      <td className="text-right py-2 text-muted-foreground">
                        {formatTime(g.totalSeconds)}
                      </td>
                      <td className="text-right py-2 text-muted-foreground">
                        {g.sessionCount.toLocaleString()}
                      </td>
                      <td className="text-right py-2 text-muted-foreground">
                        {formatAvgSession(g.totalSeconds, g.sessionCount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
